import React, { useState, useRef, useEffect } from "react";
import { API_BASE_URL } from "../config/api"; 

interface UserMenuProps {
    username: string;
    email: string;
    onLogout: () => void;
    onResetPassword: () => void;
    id: string;
    memberId: number;
}

interface MemberDetails {
    securedstichRevenueShare: number;
    clientRevenueShare: number;
    companyName: string;
}

const getInitials = (name: string): string => {
    if (!name) return "U";
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    } else {
        return name.slice(0, 2).toUpperCase();
    }
};

const UserMenu: React.FC<UserMenuProps> = ({ username, email, id, memberId, onLogout, onResetPassword }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [memberDetails, setMemberDetails] = useState<MemberDetails | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const isAdmin = email === "omkark@securedstitch.com";
    const initials = getInitials(username);

    useEffect(() => {
        if (showMenu) {
            const memberUuid = localStorage.getItem("memberUuid");
            if (!memberUuid) return;

            fetch(`${API_BASE_URL}/api/crd/memberDetails`, { // 👈 changed
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ memberUuid }),
            })
                .then((res) => res.json())
                .then((data) => setMemberDetails(data))
                .catch((error) => console.error("Error fetching member details:", error));
        }
    }, [showMenu]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setShowMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative inline-block text-left" ref={menuRef}>
            <button
                onClick={() => setShowMenu(!showMenu)}
                className="text-white font-semibold hover:opacity-80 transition-opacity flex items-center"
                aria-expanded={showMenu}
                aria-haspopup="true"
            >
                {/* Mobile: Show initials in circle */}
                <div className="md:hidden flex items-center justify-center w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded-full transition-colors text-white font-bold text-xs">
                    {initials}
                </div>

                {/* Desktop: Show welcome text */}
                <span className="hidden md:inline hover:underline">
                    Welcome, <span className="font-bold">{username}</span>
                </span>
            </button>

            {showMenu && (
                <div className="absolute top-full mt-2 right-0 w-[85vw] max-w-sm sm:w-80 md:w-96 bg-white text-black rounded-lg shadow-lg z-[9999] p-4 space-y-2 text-sm transition-all duration-300">
                    {/* User info header for mobile */}
                    <div className="md:hidden pb-2 border-b border-gray-200">
                        <p className="font-bold text-gray-800">{username}</p>
                    </div>

                    <div className="space-y-1">
                        <div>
                            <span className="font-semibold">Member Name:</span>{" "}
                            {memberDetails?.companyName || "Loading..."}
                        </div>
                        <div className="break-all">
                            <span className="font-semibold">Email:</span> {email}
                        </div>
                        <div className="break-all">
                            <span className="font-semibold">Member Uuid:</span> {id}
                        </div>
                        {!isAdmin && (
                            <div>
                                <span className="font-semibold">Revenue Share:</span>{" "}
                                {memberDetails?.clientRevenueShare || 0}%
                            </div>
                        )}
                    </div>
                    <hr className="border-gray-300" />
                    <button
                        className="w-full text-left text-blue-600 hover:underline break-all"
                        onClick={() => (window.location.href = "mailto:contact@securedstitch.com")}
                    >
                        Contact Us: contact@securedstitch.com
                    </button>
                    <button onClick={onLogout} className="w-full text-left text-red-600 hover:underline">
                        Logout
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserMenu;